import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';

import { AuthProvider, useAuth } from './lib/auth';
import { UnitsProvider } from './lib/units';

import Landing  from './pages/Landing';
import Activate from './pages/Activate';
import Forgot   from './pages/Forgot';
import Reset    from './pages/Reset';

// Authenticated pages — lightweight stubs for now so the dev server runs
// end-to-end while D-6..D-10 are built out in subsequent steps.
import Dashboard from './pages/Dashboard';
import Statement from './pages/Statement';
import Upload    from './pages/Upload';
import Profile   from './pages/Profile';

export default function App() {
    return (
        <AuthProvider>
            <UnitsProvider>
                <BootGate>
                    <Routes>
                        <Route path="/"         element={<GuestOnly><Landing /></GuestOnly>} />
                        <Route path="/activate" element={<Activate />} />
                        <Route path="/forgot"   element={<GuestOnly><Forgot /></GuestOnly>} />
                        <Route path="/reset"    element={<Reset />} />

                        <Route path="/app"            element={<Protected><Dashboard /></Protected>} />
                        <Route path="/app/statement"  element={<Protected><Statement /></Protected>} />
                        <Route path="/app/upload"     element={<Protected><Upload /></Protected>} />
                        <Route path="/app/profile"    element={<Protected><Profile /></Protected>} />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BootGate>
            </UnitsProvider>
        </AuthProvider>
    );
}

// While the auth provider is performing its silent /api/auth/refresh, hold the
// page on an atmospheric splash so we never flash the landing for an already
// signed-in user.
function BootGate({ children }: { children: ReactNode }) {
    const { status } = useAuth();
    if (status === 'unknown') return <Splash />;
    return <>{children}</>;
}

function Splash() {
    return (
        <div className="min-h-screen grid place-items-center bg-slate-base">
            <div className="text-center">
                <div className="font-display text-5xl text-ink-on tracking-tight">EIT</div>
                <div className="mt-4 h-px w-12 mx-auto bg-ember animate-ember-pulse" />
            </div>
        </div>
    );
}

function Protected({ children }: { children: ReactNode }) {
    const { status } = useAuth();
    if (status !== 'authed') return <Navigate to="/" replace />;
    return <>{children}</>;
}

function GuestOnly({ children }: { children: ReactNode }) {
    const { status } = useAuth();
    if (status === 'authed') return <Navigate to="/app" replace />;
    return <>{children}</>;
}
